import React, { useState, useMemo, useCallback, useRef } from 'react';
import { toast } from 'components/Toast';
import axiosInstance from '../../axios/axiosInstance';
import company from "assets/images/icon/company.png";
import companystatus from "assets/images/icon/company-status.png";
import onboard from "assets/images/icon/onboard.png";
import implementation from "assets/images/icon/implementation.png";
import Card from '../../components/Card';
import { Card as Bcard, Row, Col, Modal, Button } from "react-bootstrap";
import edit from 'assets/images/icon/edit-orange.png';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { getContractor, getContractorBankAccounts } from 'services/contractors';
import PlaidLinkButton from 'components/PlaidLinkButton';
import { getPayrolls, previewPayroll } from 'services/payrolls';
import routes from 'constants/routes';
import { stringMerger } from 'utils';
import { createNotification } from 'services/timeOff';

const ONBOARDING_API = 'https://onboarding-api.payrollnoot.com';

function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
}

function maskSSN(ssn) {
    if (!ssn) return '-';
    const clean = ssn.replace(/\D/g, '');
    return clean.length >= 4 ? '****** ' + clean.slice(-4) : '******';
}

const TABS = ['Paystubs', 'Documents', 'Workplaces', 'Benefits', 'Misc DED', 'Bank Accounts', 'W-9'];
const unwrap = (res) => Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);
const fmtMoney = (v) => v != null ? '$' + Number(v).toFixed(2) : '-';

const STUB_PRINT_ID = 'contractor-stub-printable';

function handlePayStubPrint() {
    const el = document.getElementById(STUB_PRINT_ID);
    if (!el) return;
    const w = window.open('', '_blank', 'width=860,height=600');
    w.document.write('<!DOCTYPE html><html><head><title>Contractor Pay Stub</title><style>body{font-family:Arial,sans-serif;font-size:13px;color:#222;padding:24px;}table{width:100%;border-collapse:collapse;margin-bottom:12px;}td,th{padding:5px 8px;}.text-end{text-align:right;}.fw-bold{font-weight:700;}.stub-header{background:#fef9f5;border:1px solid #f0d0b0;border-radius:8px;padding:12px 16px;display:flex;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:16px;}.net-bar{background:#f0faf0;border:1px solid #c3e6c3;border-radius:8px;padding:12px 16px;display:flex;justify-content:space-between;align-items:center;margin-top:12px;}</style></head><body>');
    w.document.write(el.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    w.focus();
    w.print();
    w.close();
}

const PAY_RATE_UNIT = (payType) => {
    switch ((payType || '').toLowerCase()) {
        case 'hourly':      return '/ hr';
        case 'weekly':      return '/ wk';
        case 'biweekly':    return '/ 2 wks';
        case 'semimonthly': return '/ semi-mo';
        case 'monthly':     return '/ mo';
        case 'annually':
        case 'salary':      return '/ yr';
        case 'flatfee':
        case 'flat':        return 'flat';
        default:            return '';
    }
};

function ContractorPayStubModal({ payroll, contractorId, onClose }) {
    const [downloading, setDownloading] = useState(false);

    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            const companyId = localStorage.getItem('companyId') || '';
            const res = await axiosInstance.get(
                `/companies/${companyId}/contractors/${contractorId}/paystubs/${payroll.id}/download`,
                { responseType: 'blob' }
            );
            const contentType = res.headers['content-type'] || 'application/pdf';
            const isPdf = contentType.includes('pdf');
            const blob = new Blob([res.data], { type: contentType });
            const url  = URL.createObjectURL(blob);
            const name = [contractorProfile?.businessName, contractorProfile?.firstName, contractorProfile?.lastName].filter(Boolean).join('-') || 'contractor';
            const date = payroll.payDate ? payroll.payDate.slice(0, 10) : payroll.id;
            const dlName = `paystub-${name}-${date}.pdf`;
            if (isPdf) {
                const a = document.createElement('a');
                a.href = url; a.download = dlName;
                document.body.appendChild(a); a.click(); a.remove();
            } else {
                const tab = window.open(url, '_blank');
                if (!tab) {
                    const a = document.createElement('a');
                    a.href = url; a.download = dlName;
                    document.body.appendChild(a); a.click(); a.remove();
                }
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { toast('Failed to download PDF. Please try printing instead.', 'error'); }
        finally { setDownloading(false); }
    };

    const { data: calc, isLoading, isError } = useQuery({
        queryKey: ['preview', payroll.id],
        queryFn: () => previewPayroll(payroll.id),
    });

    const { data: contractorProfile } = useQuery({
        queryKey: ['contractor', contractorId],
        queryFn: () => getContractor(contractorId),
        enabled: !!contractorId,
        staleTime: 5 * 60 * 1000,
    });

    const cData = useMemo(() => {
        if (!calc?.contractors) return null;
        return calc.contractors.find(c => c.contractorId === contractorId || c.id === contractorId);
    }, [calc, contractorId]);

    const period = formatDate(payroll.startDate) + ' - ' + formatDate(payroll.endDate);
    const payDate = formatDate(payroll.payDate);
    const grossPay = cData ? (cData.grossPay ?? ((cData.hours ?? 0) * (cData.payRate ?? 0))) : 0;
    const payType = cData?.payType || contractorProfile?.payType || '';

    return (
        <Modal show onHide={onClose} size="lg" centered>
            <Modal.Header closeButton style={{ background: '#1B75F0', color: '#fff' }}>
                <Modal.Title style={{ fontSize: 16 }}>Contractor Pay Stub</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 13 }}>
                {isLoading && <p className="text-center text-muted py-4">Loading pay stub...</p>}
                {isError && <p className="text-center text-danger py-4">Failed to load pay stub data.</p>}
                {!isLoading && !isError && !cData && (
                    <p className="text-center text-muted py-4">No pay stub found for this contractor in this payroll.</p>
                )}
                {cData && (
                    <div id={STUB_PRINT_ID}>
                        <div className="d-flex justify-content-between mb-3 p-3 flex-wrap gap-2"
                            style={{ background: '#fef9f5', borderRadius: 8, border: '1px solid #f0d0b0' }}>
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Contractor</div>
                                <div className="fw-bold">
                                    {cData.firstName} {cData.lastName}
                                    {cData.businessName ? ' (' + cData.businessName + ')' : ''}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pay Period</div>
                                <div className="fw-bold">{period}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pay Date</div>
                                <div className="fw-bold">{payDate}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Contractor Type</div>
                                <div className="fw-bold">1099 / Independent</div>
                            </div>
                            {cData.paymentMethod && (
                                <div>
                                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Payment Method</div>
                                    <div className="fw-bold">{(cData.paymentMethod || '').replace(/_/g, ' ').replace(/\b\w/g, function(c){ return c.toUpperCase(); })}</div>
                                </div>
                            )}
                        </div>

                        <div className="fw-bold mb-2" style={{ borderBottom: '2px solid #1B75F0', paddingBottom: 4 }}>Earnings</div>
                        <table className="table table-sm">
                            <tbody>
                                {cData.hours > 0 && (
                                    <tr>
                                        <td>Hours Worked</td>
                                        <td className="text-end">{cData.hours} hrs</td>
                                    </tr>
                                )}
                                {cData.payRate != null && (
                                    <tr>
                                        <td>Pay Rate</td>
                                        <td className="text-end">
                                            {fmtMoney(cData.payRate)}{PAY_RATE_UNIT(payType) ? ` ${PAY_RATE_UNIT(payType)}` : ''}
                                        </td>
                                    </tr>
                                )}
                                {cData.reimbursement > 0 && (
                                    <tr>
                                        <td>Reimbursement</td>
                                        <td className="text-end">{fmtMoney(cData.reimbursement)}</td>
                                    </tr>
                                )}
                                <tr style={{ borderTop: '2px solid #dee2e6', fontWeight: 700 }}>
                                    <td>Gross Pay</td>
                                    <td className="text-end">{fmtMoney(grossPay)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="p-2 mb-3" style={{ background: '#fff8f0', borderRadius: 6, border: '1px solid #ffe0b2', fontSize: 12, color: '#888' }}>
                            As a 1099 independent contractor, no federal or state taxes are withheld. The contractor is responsible for self-employment taxes.
                        </div>

                        <div className="d-flex justify-content-between align-items-center p-3"
                            style={{ background: '#f0faf0', borderRadius: 8, border: '1px solid #c3e6c3' }}>
                            <div style={{ fontSize: 12, color: '#555' }}>No tax withholdings (1099 contractor)</div>
                            <div>
                                <span className="text-muted me-2" style={{ fontSize: 13 }}>Total Payment</span>
                                <span className="fw-bold" style={{ fontSize: 22, color: '#2e7d32' }}>{fmtMoney(grossPay)}</span>
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
                {cData && (
                    <>
                        <Button
                            size="sm"
                            style={{ backgroundColor: '#1a9fbb', border: 'none', opacity: downloading ? 0.7 : 1 }}
                            onClick={handleDownloadPdf}
                            disabled={downloading}
                        >
                            {downloading ? 'Downloading...' : 'Download PDF'}
                        </Button>
                        <Button size="sm" style={{ backgroundColor: '#1B75F0', border: 'none' }} onClick={handlePayStubPrint}>Print</Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function printW9(c) {
    const w = window.open('', '_blank', 'width=900,height=700');
    const name = [c.firstName, c.lastName].filter(Boolean).join(' ');
    const addr = [c.addressLine1 || c.address || c.street1, c.city, c.state, c.zip || c.zipcode].filter(Boolean).join(', ');
    const taxClass = c.businessType || c.taxClassification || (c.businessName ? 'LLC' : 'Individual');

    w.document.write(`<!DOCTYPE html><html><head><title>W-9 — ${name || c.businessName}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; color: #000; padding: 20px; max-width: 800px; margin: 0 auto; }
  h2 { text-align: center; font-size: 15px; border-bottom: 2px solid #000; padding-bottom: 6px; }
  h3 { font-size: 13px; background: #e0e0e0; padding: 4px 8px; margin: 14px 0 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  td, th { border: 1px solid #999; padding: 5px 8px; }
  th { background: #f0f0f0; text-align: left; }
  .note { font-size: 10px; color: #555; margin-top: 16px; padding-top: 8px; border-top: 1px solid #ccc; }
  .box { border: 1px solid #000; padding: 3px 8px; display: inline-block; min-width: 18px; text-align: center; margin-right: 4px; }
  .sig-line { border-bottom: 1px solid #000; margin: 20px 0 4px; height: 30px; }
</style></head><body>
<h2>Form W-9 — Request for Taxpayer Identification Number and Certification<br/>
<span style="font-size:11px;">Department of the Treasury — Internal Revenue Service</span></h2>

<p style="font-size:11px;margin:0 0 10px;">Under penalties of perjury, I certify that: (1) The number shown on this form is my correct TIN. (2) I am not subject to backup withholding. (3) I am a U.S. citizen or other U.S. person.</p>

<h3>Part I — Taxpayer Identification</h3>
<table>
  <tr>
    <th style="width:50%">Name (as shown on your income tax return)</th>
    <th>Business name (if different from above)</th>
  </tr>
  <tr>
    <td>${name || c.businessName || ''}</td>
    <td>${c.businessName || ''}</td>
  </tr>
  <tr>
    <th>Federal Tax Classification</th>
    <th>Address</th>
  </tr>
  <tr>
    <td>
      <span class="box">${taxClass === 'Individual' || !c.businessName ? '✓' : ''}</span> Individual/sole proprietor or single-member LLC<br/>
      <span class="box">${taxClass === 'LLC' ? '✓' : ''}</span> LLC<br/>
      <span class="box">${taxClass === 'Corporation' ? '✓' : ''}</span> Corporation<br/>
      <span class="box">${taxClass === 'Partnership' ? '✓' : ''}</span> Partnership
    </td>
    <td>${addr}</td>
  </tr>
  <tr>
    <th>Social Security Number (SSN)</th>
    <th>Employer Identification Number (EIN)</th>
  </tr>
  <tr>
    <td>${c.ssn ? '•••-••-' + String(c.ssn).slice(-4) : (c.ein ? 'Using EIN below' : '—')}</td>
    <td>${c.ein ? '••-•••' + String(c.ein).slice(-4) : '—'}</td>
  </tr>
</table>

<h3>Part II — Certification</h3>
<p style="font-size:11px;">By signing below, I certify that all information provided is accurate.</p>
<div class="sig-line"></div>
<div style="display:flex;justify-content:space-between;font-size:11px;">
  <span>Signature of U.S. person</span>
  <span>Date: ___________________</span>
</div>

<div class="note">
  <strong>Note:</strong> Form W-9 is provided by the contractor to the payer for 1099-NEC reporting purposes.<br/>
  The payer must keep this form on file. The contractor's SSN or EIN will be used on the 1099 form filed with the IRS.
</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
}

function W9Tab({ contractor: c }) {
    if (!c) return <p className="text-muted text-center">Loading contractor data...</p>;

    const name   = [c.firstName, c.lastName].filter(Boolean).join(' ');
    const addr   = [c.addressLine1 || c.address || c.street1, c.city, c.state, c.zip || c.zipcode].filter(Boolean).join(', ');
    const hasTin = c.ssn || c.ein;

    return (
        <div style={{ fontSize: 13 }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                    <h6 className="fw-bold mb-0">Form W-9 — Request for Taxpayer Identification Number</h6>
                    <small className="text-muted">Pre-populated from contractor onboarding data. Click Print to generate the W-9.</small>
                </div>
                <button
                    className="btn btn-sm fw-semibold"
                    style={{ background: '#1B75F0', color: '#fff', border: 'none' }}
                    onClick={() => printW9(c)}
                >
                    Print / Download W-9
                </button>
            </div>

            {!hasTin && (
                <div className="alert alert-warning" style={{ fontSize: 12 }}>
                    No SSN or EIN on file for this contractor. The W-9 cannot be issued without a TIN. Please collect the contractor's TIN before generating the W-9.
                </div>
            )}

            <div className="p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div className="row g-3">
                    <div className="col-md-6">
                        <small className="text-muted d-block">Name (as on income tax return)</small>
                        <span className="fw-semibold">{name || '—'}</span>
                    </div>
                    <div className="col-md-6">
                        <small className="text-muted d-block">Business Name</small>
                        <span className="fw-semibold">{c.businessName || '—'}</span>
                    </div>
                    <div className="col-md-6">
                        <small className="text-muted d-block">Address</small>
                        <span className="fw-semibold">{addr || '—'}</span>
                    </div>
                    <div className="col-md-3">
                        <small className="text-muted d-block">SSN (masked)</small>
                        <span className="fw-semibold">{c.ssn ? `•••-••-${String(c.ssn).slice(-4)}` : '—'}</span>
                    </div>
                    <div className="col-md-3">
                        <small className="text-muted d-block">EIN (masked)</small>
                        <span className="fw-semibold">{c.ein ? `••-•••${String(c.ein).slice(-4)}` : '—'}</span>
                    </div>
                    <div className="col-md-4">
                        <small className="text-muted d-block">Tax Classification</small>
                        <span className="fw-semibold">{c.businessType || c.taxClassification || (c.businessName ? 'LLC' : 'Individual/sole proprietor')}</span>
                    </div>
                    <div className="col-md-4">
                        <small className="text-muted d-block">Contractor Type</small>
                        <span className="fw-semibold">{c.type || '1099'}</span>
                    </div>
                    <div className="col-md-4">
                        <small className="text-muted d-block">Start Date</small>
                        <span className="fw-semibold">{c.startDate ? new Date(c.startDate).toLocaleDateString() : '—'}</span>
                    </div>
                </div>
            </div>

            <div className="mt-3 p-3 rounded" style={{ background: 'rgba(232,119,34,0.06)', border: '1px solid rgba(232,119,34,0.2)', fontSize: 12 }}>
                <strong>Compliance note:</strong> This W-9 is based on information provided by the contractor during onboarding. You must obtain a signed copy from the contractor before making any payments. Retain this form in your records for at least 4 years. The contractor's TIN will be used for 1099-NEC filing at year end.
            </div>
        </div>
    );
}

function ViewContractor() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const contractorId = searchParams.get('code');
    const companyId = localStorage.getItem('companyId') || '';
    const TAB_URL_MAP = { paystubs: 0, documents: 1, workplaces: 2, benefits: 3, 'misc-ded': 4, 'bank-accounts': 5, 'w-9': 6 };
    const [activeTab, setActiveTab] = useState(() => TAB_URL_MAP[searchParams.get('tab')] ?? 0);
    const [stubPayroll, setStubPayroll] = useState(null);

    // Documents state
    const [contractorDocs, setContractorDocs] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [contractorTaxDocs, setContractorTaxDocs] = useState(null); // W-2 / 1099
    const [taxDocsLoading, setTaxDocsLoading] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [deletingDocId, setDeletingDocId] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, docId: null, fileName: '' });
    const docUploadRef = useRef(null);

    const fetchDocs = useCallback(async (email) => {
        if (!email) return;
        setDocsLoading(true);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            const resp = await fetch(`${ONBOARDING_API}/api/employee-documents?email=${encodeURIComponent(email)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await resp.json();
            setContractorDocs(Array.isArray(data) ? data : []);
        } catch { setContractorDocs([]); }
        finally { setDocsLoading(false); }
    }, []);

    const fetchTaxDocs = useCallback(async (ctrId) => {
        if (!ctrId) return;
        const cid = localStorage.getItem('companyId') || '';
        if (!cid) return;
        setTaxDocsLoading(true);
        try {
            const res = await axiosInstance.get(
                `/companies/${cid}/documents/contractor_tax_documents?contractorId=${ctrId}`
            );
            const list = res.data?.data ?? res.data ?? [];
            setContractorTaxDocs(Array.isArray(list) ? list : []);
        } catch { setContractorTaxDocs([]); }
        finally { setTaxDocsLoading(false); }
    }, []);

    const uploadDoc = async (file, email) => {
        if (!file || !email) return;
        const allowed = ['.pdf', '.doc', '.docx', '.csv', '.png', '.jpg', '.jpeg'];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowed.includes(ext)) { toast('Accepted: PDF, DOC, DOCX, CSV, PNG, JPG', 'warning'); return; }
        setUploadingDoc(true);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            const formData = new FormData();
            formData.append('file', file);
            const resp = await fetch(
                `${ONBOARDING_API}/api/employee-documents/upload?email=${encodeURIComponent(email)}`,
                { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
            );
            if (resp.ok) {
                const doc = await resp.json();
                setContractorDocs(prev => [doc, ...prev]);
                // Notify the contractor a new document is available
                const cid = localStorage.getItem('companyId');
                if (c?.id && cid) {
                    const docLabel = doc?.label || doc?.name || doc?.documentType || file.name || 'document';
                    createNotification({
                        recipientId: c.id,
                        recipientType: 'contractor',
                        type: 'document_uploaded',
                        title: 'New Document Available',
                        message: `A new document (${docLabel}) has been uploaded to your profile. You can view it in your Documents section.`,
                        link: '/contractor-portal/documents?tab=my-documents',
                        companyId: cid,
                    }).catch(() => {});
                }
            } else { toast('Upload failed. Please try again.', 'error'); }
        } catch { toast('Upload failed.', 'error'); }
        finally { setUploadingDoc(false); if (docUploadRef.current) docUploadRef.current.value = ''; }
    };

    const deleteDoc = (docId, fileName) => {
        setDeleteConfirm({ show: true, docId, fileName: fileName || 'this document' });
    };

    const confirmDelete = async () => {
        const { docId } = deleteConfirm;
        setDeleteConfirm({ show: false, docId: null, fileName: '' });
        setDeletingDocId(docId);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            await fetch(`${ONBOARDING_API}/api/employee-documents/${docId}`, {
                method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
            });
            setContractorDocs(prev => prev.filter(d => d.id !== docId));
        } catch { toast('Failed to delete document.', 'error'); }
        finally { setDeletingDocId(null); }
    };

    const { data: contractor, isLoading, isError } = useQuery({
        queryKey: ['contractor', contractorId],
        queryFn: () => getContractor(contractorId),
        enabled: !!contractorId,
        onSuccess: (data) => { if (data?.email) fetchDocs(data.email); },
    });

    // Also fetch docs when switching to Documents tab
    const prevTabRef = useRef(null);
    useMemo(() => {
        if (activeTab === 1 && prevTabRef.current !== 1 && contractor?.email) {
            fetchDocs(contractor.email);
            if (contractorTaxDocs === null && contractor?.id) fetchTaxDocs(contractor.id);
        }
        prevTabRef.current = activeTab;
    }, [activeTab, contractor, fetchDocs, fetchTaxDocs, contractorTaxDocs]);

    const { data: bankAccountsRaw } = useQuery({
        queryKey: ['contractor-bank-accounts', contractorId],
        queryFn: () => getContractorBankAccounts(contractorId),
        enabled: !!contractorId,
        staleTime: 5 * 60 * 1000,
    });
    const contractorBankAccounts = Array.isArray(bankAccountsRaw) ? bankAccountsRaw : (bankAccountsRaw?.data ?? bankAccountsRaw?.items ?? []);

    const { data: payrollsRaw } = useQuery({ queryKey: ['payrolls'], queryFn: getPayrolls });
    const approvedPayrolls = useMemo(() => unwrap(payrollsRaw).filter(p => p.status === 'approved'), [payrollsRaw]);

    // Fetch previews for all approved payrolls (only when Paystubs tab is active)
    // so we can filter to only payrolls where this contractor has a record.
    const previewQueries = useQueries({
        queries: activeTab === 0 ? approvedPayrolls.map(p => ({
            queryKey: ['preview', p.id],
            queryFn: () => previewPayroll(p.id),
            staleTime: 5 * 60 * 1000,
        })) : [],
    });

    const previewsLoading = activeTab === 0 && previewQueries.some(q => q.isLoading);

    // Only show payrolls where this contractor actually has a payment record
    const contractorPayrolls = useMemo(() => {
        if (activeTab !== 0 || previewsLoading) return [];
        return approvedPayrolls.filter((_, i) => {
            const preview = previewQueries[i]?.data;
            if (!preview?.contractors) return false;
            return preview.contractors.some(c => c.contractorId === contractorId || c.id === contractorId);
        });
    }, [approvedPayrolls, previewQueries, previewsLoading, contractorId, activeTab]);

    // Compute gross pay & reimbursements from approved payroll previews (must be before early returns)
    const totalGrossPay = useMemo(() => {
        return previewQueries.reduce((sum, q) => {
            const entry = q.data?.contractors?.find(x => x.contractorId === contractorId || x.id === contractorId);
            if (!entry) return sum;
            return sum + (entry.grossPay ?? ((entry.hours ?? 0) * (entry.payRate ?? 0)));
        }, 0);
    }, [previewQueries, contractorId]);

    const totalReimbursements = useMemo(() => {
        return previewQueries.reduce((sum, q) => {
            const entry = q.data?.contractors?.find(x => x.contractorId === contractorId || x.id === contractorId);
            return sum + (entry?.reimbursement ?? 0);
        }, 0);
    }, [previewQueries, contractorId]);

    if (isLoading) {
        return <div className="content" style={{ padding: '16px 24px' }}><p>Loading contractor...</p></div>;
    }

    if (isError || !contractor) {
        return <div className="content" style={{ padding: '16px 24px' }}><p className="text-danger">Failed to load contractor data.</p></div>;
    }

    const c = contractor;
    const fullName = [c.firstName, c.middleName, c.lastName].filter(Boolean).join(' ') || '-';
    const residence = [c.addressLine1, c.city, c.state, c.zip].filter(Boolean).join(', ') || '-';

    const userDetail = [
        { title: 'Gross Pay',        count: totalGrossPay > 0 ? '$' + totalGrossPay.toFixed(2) : c.grossPay ?? 0,         icon: company },
        { title: 'Reimbursements',   count: totalReimbursements > 0 ? '$' + totalReimbursements.toFixed(2) : c.reimbursements ?? 0, icon: companystatus },
        { title: 'Company Benefits', count: c.benefitsCount ?? 0,  icon: onboard },
        { title: 'Company Taxes',    count: c.taxesCount ?? 0,     icon: implementation },
    ];

    const onboardStatusRaw = c.onboardingStatus || c.onboardStatus || c.status || '';
    const onboardStatus = (() => {
        if (['approved', 'active', 'completed'].includes(onboardStatusRaw?.toLowerCase())) return 'Approved';
        if (onboardStatusRaw?.toLowerCase() === 'pending_review') return 'Pending Review';
        if (onboardStatusRaw?.toLowerCase() === 'rejected') return 'Rejected';
        return onboardStatusRaw || (c.startDate ? 'Pending' : '—');
    })();
    const activeStatus = c.active ? 'Active' : 'Inactive';

    // New unified onboarding approval workflow
    const isPendingReview = c.onboardingStatus === 'pending_review';

    // Show setup banner when payRate or payType is missing — contractor can't be paid without them
    const needsAdminSetup = !isPendingReview && (!c.payRate || Number(c.payRate) === 0);

    return (
        <div className='content' style={{ padding: "16px 24px 16px 16px" }}>

            {/* Delete document confirmation modal */}
            {deleteConfirm.show && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(0,0,0,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px',
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 16, padding: '32px 28px 24px',
                        maxWidth: 400, width: '100%', textAlign: 'center',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    }}>
                        <div style={{ fontSize: 44, marginBottom: 12 }}>🗑️</div>
                        <h5 style={{ margin: '0 0 8px', fontWeight: 700, color: '#111827' }}>Delete Document</h5>
                        <p style={{ margin: '0 0 24px', fontSize: 14, color: '#6b7280', lineHeight: 1.5 }}>
                            Are you sure you want to delete <strong>{deleteConfirm.fileName}</strong>? This cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                            <button
                                onClick={() => setDeleteConfirm({ show: false, docId: null, fileName: '' })}
                                style={{ minWidth: 90, fontWeight: 600, padding: '8px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                            >Cancel</button>
                            <button
                                onClick={confirmDelete}
                                style={{ minWidth: 90, fontWeight: 600, padding: '8px 20px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 14 }}
                            >Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="start-new-payroll-section">
                <div>
                    <div className="row mt-2">
                        {userDetail.map((item) => (
                            <Card key={item.title} title={item.title} count={item.count} icon={item.icon} />
                        ))}
                    </div>
                </div>

                {/* Pending Review banner — shown when onboardingStatus = pending_review */}
                {isPendingReview && (
                    <div style={{
                        display:'flex', alignItems:'flex-start', gap:14,
                        background:'#EFF6FF', border:'2px solid #1B75F0',
                        borderRadius:12, padding:'14px 18px', marginTop:16,
                    }}>
                        <span style={{ fontSize:22, lineHeight:1 }}>👤</span>
                        <div style={{ flex:1 }}>
                            <div style={{ fontWeight:700, color:'#c2410c', fontSize:14, marginBottom:3 }}>
                                Pending Onboarding Approval
                            </div>
                            <div style={{ fontSize:13, color:'#7c2d12', lineHeight:1.6 }}>
                                This contractor completed self-onboarding and is awaiting your review. They are currently <strong>excluded from payroll</strong> until approved.
                            </div>
                        </div>
                        <button
                            onClick={() => navigate(routes.ONBOARDING.HOME)}
                            style={{ fontSize:13, color:'#fff', fontWeight:700, whiteSpace:'nowrap', alignSelf:'center', background:'#1B75F0', border:'none', borderRadius:20, padding:'8px 18px', cursor:'pointer' }}
                        >
                            Review &amp; Approve →
                        </button>
                    </div>
                )}

                {/* Incomplete Setup banner — shown when payRate / payType not yet configured */}
                {needsAdminSetup && (
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: 14,
                        background: '#EFF6FF', border: '2px solid #1B75F0',
                        borderRadius: 12, padding: '14px 18px', marginTop: 16,
                    }}>
                        <span style={{ fontSize: 22, lineHeight: 1 }}>⚙️</span>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, color: '#c2410c', fontSize: 14, marginBottom: 3 }}>
                                Incomplete Setup — Admin Action Required
                            </div>
                            <div style={{ fontSize: 13, color: '#7c2d12', lineHeight: 1.6 }}>
                                This contractor completed onboarding. Please finalize the following fields before running payroll:
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 8 }}>
                                {[
                                    { label: 'Pay rate',        ok: !!(c.payRate && Number(c.payRate) > 0) },
                                    { label: 'Pay type',        ok: !!(c.payType) },
                                    { label: 'Payment method',  ok: !!(c.defaultPaymentMethod || c.paymentMethod) },
                                    { label: 'Active status',   ok: !!c.active },
                                ].map(({ label, ok }) => (
                                    <span key={label} style={{
                                        fontSize: 12, fontWeight: 600, padding: '3px 10px',
                                        borderRadius: 20, border: '1.5px solid',
                                        borderColor: ok ? '#16a34a' : '#1B75F0',
                                        color: ok ? '#15803d' : '#c2410c',
                                        background: ok ? '#f0fdf4' : '#EFF6FF',
                                    }}>
                                        {ok ? '✓' : '○'} {label}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <button
                            onClick={() => navigate(stringMerger(routes.CONTRACTOR.HOME, routes.CONTRACTOR.ADD_EDIT_CONTRACTOR) + '?id=' + c.id + '&highlight=setup')}
                            style={{ fontSize: 13, color: '#1B75F0', fontWeight: 700, whiteSpace: 'nowrap', alignSelf: 'center', background: '#EFF6FF', border: '1.5px solid #1B75F0', borderRadius: 20, padding: '6px 14px', cursor: 'pointer' }}
                        >
                            ✏️ Complete Setup →
                        </button>
                    </div>
                )}

                <div className='d-flex flex-row gap-4 mt-3 view-page-layout'>
                    <Bcard className="employee-card p-4" style={{ width: '100%' }}>
                        <Row className="align-items-center">
                            <Col>
                                <small className="text-muted badge-title">Contractor ID</small>
                                <div className="fw-bold" style={{ fontFamily: 'monospace', fontSize: 13 }}>
                                    {c.id ? (c.id.length > 8 ? `••••${c.id.slice(-6)}` : c.id) : '—'}
                                </div>
                            </Col>
                            <Col xs="auto">
                                <img src={edit} alt="Edit" style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(stringMerger(routes.CONTRACTOR.HOME, routes.CONTRACTOR.ADD_EDIT_CONTRACTOR) + '?id=' + c.id)} />
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col className='d-flex flex-column col-lg-3'>
                                <span className='badge-title'>Onboard Status</span>
                                <span className={'badge ' + (['Approved', 'Completed'].includes(onboardStatus) ? 'status-completed' : onboardStatus === 'Rejected' ? 'status-no-user' : onboardStatus === '—' ? 'status-no-user' : 'status-active')}>
                                    {onboardStatus}
                                </span>
                            </Col>
                            <Col className='d-flex flex-column col-lg-3'>
                                <span className='badge-title'>Active Status</span>
                                <span className={'badge ' + (c.active ? 'status-completed' : 'status-no-user')}>{activeStatus}</span>
                            </Col>
                            <Col className='d-flex flex-column col-lg-3'>
                                <span className='badge-title'>Portal Account</span>
                                <span className={'badge ' + (c.paymentPortalStatus === 'Active' ? 'status-completed' : c.paymentPortalStatus === 'Inactive' ? 'status-active' : 'status-no-user')}>
                                    {c.paymentPortalStatus || 'No User'}
                                </span>
                            </Col>
                        </Row>

                        <hr />
                        <h6 className="fw-bold">General Information</h6>
                        <Row>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Full Name</small><br />
                                <span className='badge-value badge-title'>{fullName}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Contractor Type</small><br />
                                <span className='badge-value badge-title'>{c.type || '-'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Payment Method</small><br />
                                <span className='badge-value badge-title'>{contractorBankAccounts.length > 0 ? 'Direct Deposit' : (c.defaultPaymentMethod || c.paymentMethod || 'Check').replace(/_/g, ' ').replace(/\b\w/g, function(ch){ return ch.toUpperCase(); })}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">SSN / EIN</small><br />
                                <span className='badge-value badge-title'>{maskSSN(c.ssn || c.ein)}</span>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Date of Birth</small><br />
                                <span className='badge-value badge-title'>{formatDate(c.dateOfBirth || c.dob)}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Start Date</small><br />
                                <span className='badge-value badge-title'>{formatDate(c.startDate || c.hireDate)}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Termination Date</small><br />
                                <span className='badge-value badge-title'>{formatDate(c.terminationDate)}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Pay Rate / Type</small><br />
                                <span className='badge-value badge-title'>{c.payRate ? fmtMoney(c.payRate) : '-'} / {c.payType || '-'}</span>
                            </Col>
                        </Row>

                        {/* Business Info — shown for Business type */}
                        {(c.type === 'Business' || c.businessName || c.tradeName || c.serviceType || c.businessAddress) && (
                            <>
                                <hr />
                                <h6 className="fw-bold">Business Information</h6>
                                <Row>
                                    {c.businessName && (
                                        <Col className='col-lg-3'>
                                            <small className="text-muted badge-title">Business / Legal Name</small><br />
                                            <span className='badge-value badge-title'>{c.businessName}</span>
                                        </Col>
                                    )}
                                    {c.tradeName && (
                                        <Col className='col-lg-3'>
                                            <small className="text-muted badge-title">Trade Name (DBA)</small><br />
                                            <span className='badge-value badge-title'>{c.tradeName}</span>
                                        </Col>
                                    )}
                                    {c.serviceType && (
                                        <Col className='col-lg-3'>
                                            <small className="text-muted badge-title">Service / Work Type</small><br />
                                            <span className='badge-value badge-title'>{c.serviceType}</span>
                                        </Col>
                                    )}
                                    {c.businessAddress && (
                                        <Col className='col-lg-3'>
                                            <small className="text-muted badge-title">Business Address</small><br />
                                            <span className='badge-value badge-title'>{c.businessAddress}</span>
                                        </Col>
                                    )}
                                </Row>
                            </>
                        )}

                        <hr />
                        <h6 className="fw-bold">Contact Information</h6>
                        <Row>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Address</small><br />
                                <span className='badge-value badge-title'>{residence}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Email</small><br />
                                <span className='badge-value badge-title'>{c.email || '-'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Cell Phone</small><br />
                                <span className='badge-value badge-title'>{c.cellPhone || c.phone || '-'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Home Phone</small><br />
                                <span className='badge-value badge-title'>{c.homePhone || '-'}</span>
                            </Col>
                        </Row>

                        {/* Emergency Contact — shown when any field is populated */}
                        {(c.emergencyFirstName || c.emergencyLastName || c.emergencyPhone) && (
                            <>
                                <hr />
                                <h6 className="fw-bold">Emergency Contact</h6>
                                <Row>
                                    <Col className='col-lg-3'>
                                        <small className="text-muted badge-title">Name</small><br />
                                        <span className='badge-value badge-title'>
                                            {[c.emergencyFirstName, c.emergencyLastName].filter(Boolean).join(' ') || '-'}
                                        </span>
                                    </Col>
                                    {c.emergencyRelationship && (
                                        <Col className='col-lg-3'>
                                            <small className="text-muted badge-title">Relationship</small><br />
                                            <span className='badge-value badge-title'>{c.emergencyRelationship}</span>
                                        </Col>
                                    )}
                                    {c.emergencyPhone && (
                                        <Col className='col-lg-3'>
                                            <small className="text-muted badge-title">Primary Phone</small><br />
                                            <span className='badge-value badge-title'>{c.emergencyPhone}</span>
                                        </Col>
                                    )}
                                    {c.emergencyAltPhone && (
                                        <Col className='col-lg-3'>
                                            <small className="text-muted badge-title">Alternate Phone</small><br />
                                            <span className='badge-value badge-title'>{c.emergencyAltPhone}</span>
                                        </Col>
                                    )}
                                </Row>
                                {c.emergencyEmail && (
                                    <Row className="mt-3">
                                        <Col className='col-lg-3'>
                                            <small className="text-muted badge-title">Email</small><br />
                                            <span className='badge-value badge-title'>{c.emergencyEmail}</span>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        )}
                    </Bcard>

                    <Bcard className="employee-card p-3" style={{ width: '80%' }}>
                        <ul className="d-flex justify-content-between tab" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {TABS.map((tab, i) => (
                                <li key={tab} className={'nav-link' + (activeTab === i ? ' active' : '')}
                                    style={{ cursor: 'pointer' }} onClick={() => setActiveTab(i)}
                                    aria-current={activeTab === i ? 'page' : undefined}>{tab}</li>
                            ))}
                        </ul>

                        <div className="mt-3">
                            {activeTab === 0 && (
                                previewsLoading
                                    ? <p className="text-muted text-center">Loading paystubs...</p>
                                    : contractorPayrolls.length === 0
                                        ? <p className="text-muted text-center">No paystubs available.</p>
                                        : <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr><th>Period</th><th>Pay Date</th><th></th></tr>
                                            </thead>
                                            <tbody>
                                                {contractorPayrolls.map(p => (
                                                    <tr key={p.id}>
                                                        <td>{formatDate(p.startDate)} &mdash; {formatDate(p.endDate)}</td>
                                                        <td>{formatDate(p.payDate)}</td>
                                                        <td>
                                                            <button className="btn btn-sm"
                                                                style={{ backgroundColor: '#1B75F0', color: '#fff', fontSize: 12 }}
                                                                onClick={() => setStubPayroll(p)}>Pay Stub</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                            )}
                            {activeTab === 1 && (
                                <div>
                                    {/* Upload button */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span style={{ fontSize: 13, color: '#555' }}>{contractorDocs.length} document{contractorDocs.length !== 1 ? 's' : ''}</span>
                                        <label style={{ background: '#1B75F0', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            {uploadingDoc ? '⏳ Uploading...' : '+ Upload Document'}
                                            <input ref={docUploadRef} type="file" style={{ display: 'none' }}
                                                accept=".pdf,.doc,.docx,.csv,.png,.jpg,.jpeg"
                                                disabled={uploadingDoc}
                                                onChange={e => { if (e.target.files[0]) uploadDoc(e.target.files[0], c.email); }} />
                                        </label>
                                    </div>

                                    {docsLoading && <p className="text-muted text-center" style={{ fontSize: 13 }}>Loading documents...</p>}

                                    {!docsLoading && contractorDocs.length === 0 && (
                                        <p className="text-muted text-center" style={{ fontSize: 13 }}>No documents uploaded yet.</p>
                                    )}

                                    {!docsLoading && contractorDocs.map(doc => (
                                        <div key={doc.id} className="d-flex justify-content-between align-items-center p-2 mb-2"
                                            style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa', fontSize: 13 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <span style={{ fontSize: 18 }}>
                                                    {/\.(png|jpg|jpeg)$/i.test(doc.fileName || doc.filename || '') ? '🖼️' : /\.pdf$/i.test(doc.fileName || doc.filename || '') ? '📄' : '📎'}
                                                </span>
                                                <div>
                                                    <div style={{ fontWeight: 500, color: '#111' }}>{doc.fileName || doc.filename || doc.name || 'Document'}</div>
                                                    <div style={{ fontSize: 11, color: '#888' }}>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : ''}</div>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                {(doc.fileUrl || doc.url || doc.FileUrl) && (() => {
                                                    const raw = doc.fileUrl || doc.url || doc.FileUrl || '';
                                                    const href = raw.startsWith('http') ? raw : `${ONBOARDING_API}${raw}`;
                                                    return <a href={href} target="_blank" rel="noreferrer"
                                                        style={{ fontSize: 12, color: '#1B75F0', fontWeight: 600, textDecoration: 'none' }}>View</a>;
                                                })()}
                                                <button onClick={() => deleteDoc(doc.id, doc.fileName || doc.filename || doc.name)}
                                                    disabled={deletingDocId === doc.id}
                                                    style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: '0 4px' }}>
                                                    {deletingDocId === doc.id ? '...' : '✕'}
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* ── Tax Documents (1099 / W-2) ── */}
                                    <div className="mt-4">
                                        <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                                            <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>Tax Documents</span>
                                            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>(1099-NEC / W-2 — published by employer)</span>
                                        </div>
                                        {taxDocsLoading ? (
                                            <p className="text-muted text-center py-2" style={{ fontSize: 13 }}>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                                Loading tax documents...
                                            </p>
                                        ) : contractorTaxDocs === null ? (
                                            <p className="text-muted" style={{ fontSize: 13 }}>Click the Documents tab to load.</p>
                                        ) : contractorTaxDocs.length === 0 ? (
                                            <p className="text-muted" style={{ fontSize: 13 }}>No published tax documents yet.</p>
                                        ) : (
                                            <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                                <thead style={{ background: '#f8f9fa' }}>
                                                    <tr>
                                                        <th>Document</th>
                                                        <th>Year</th>
                                                        <th>Published</th>
                                                        <th style={{ width: 100 }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {contractorTaxDocs.map(doc => {
                                                        const cid = localStorage.getItem('companyId') || '';
                                                        const dlUrl = `/companies/${cid}/documents/contractor_tax_documents/${doc.id}/download`;
                                                        return (
                                                            <tr key={doc.id}>
                                                                <td>
                                                                    <span style={{ fontSize: 15, marginRight: 6 }}>📋</span>
                                                                    {doc.label || `Tax Document #${doc.id}`}
                                                                </td>
                                                                <td>{doc.taxYear || doc.year || '—'}</td>
                                                                <td>{doc.filedOn ? new Date(doc.filedOn).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—'}</td>
                                                                <td>
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        style={{ backgroundColor: '#1B75F0', color: '#fff', fontSize: 11, padding: '3px 10px' }}
                                                                        onClick={async () => {
                                                                            try {
                                                                                const res = await axiosInstance.get(dlUrl, { responseType: 'blob' });
                                                                                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                                                                const a = document.createElement('a');
                                                                                a.href = url;
                                                                                a.download = `${doc.label || 'tax-document'}.pdf`;
                                                                                document.body.appendChild(a);
                                                                                a.click();
                                                                                a.remove();
                                                                                URL.revokeObjectURL(url);
                                                                            } catch { toast('Failed to download document.', 'error'); }
                                                                        }}
                                                                    >
                                                                        Download
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            )}
                            {activeTab === 2 && (
                                <div>
                                    {c.workplaceId || c.primaryWorkplaceId
                                        ? <p className="badge-value">Workplace ID: {c.primaryWorkplaceId || c.workplaceId}</p>
                                        : <p className="text-muted text-center">No workplace assigned.</p>}
                                    {c.workLocation && <p className="badge-value">Work Location: {c.workLocation}</p>}
                                </div>
                            )}
                            {activeTab === 3 && <p className="text-muted text-center">Benefits are not applicable for 1099 contractors.</p>}
                            {activeTab === 4 && <p className="text-muted text-center">Deductions are not applicable for 1099 contractors.</p>}

                            {activeTab === 5 && (
                                <div>
                                    <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                                        Bank accounts on file for direct deposit. Use Plaid to verify accounts for ACH disbursements via Dwolla.
                                    </p>
                                    {contractorBankAccounts.length === 0 ? (
                                        <p className="text-muted text-center">No bank accounts on file.</p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {contractorBankAccounts.map((acct, idx) => (
                                                <div key={acct.id ?? idx} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', background: '#fafafa' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                                                                {acct.institutionName || 'Bank Account'}
                                                                &nbsp;
                                                                <span style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', textTransform: 'capitalize' }}>
                                                                    ({acct.subtype || acct.accountType || 'checking'})
                                                                </span>
                                                            </div>
                                                            <div style={{ fontSize: 13, color: '#6b7280' }}>
                                                                ••••{String(acct.accountNumber ?? '').slice(-4)}
                                                                {acct.routingNumber && <>&nbsp;&nbsp;|&nbsp;&nbsp;Routing: {acct.routingNumber}</>}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            {acct.isVerified ? (
                                                                <span style={{ background: '#16a34a', color: '#fff', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                                                                    Plaid Verified
                                                                </span>
                                                            ) : (
                                                                <PlaidLinkButton
                                                                    userId={contractorId}
                                                                    bankAccountId={acct.id}
                                                                    ownerType="contractor"
                                                                    companyId={companyId}
                                                                    label="Verify with Plaid"
                                                                    onSuccess={() => queryClient.invalidateQueries({ queryKey: ['contractor-bank-accounts', contractorId] })}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {activeTab === 6 && (
                                <W9Tab contractor={contractor} />
                            )}
                        </div>
                    </Bcard>
                </div>
            </div>

            {stubPayroll && (
                <ContractorPayStubModal payroll={stubPayroll} contractorId={contractorId} onClose={() => setStubPayroll(null)} />
            )}
        </div>
    );
}

export default ViewContractor;

